/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn, IDetailsListProps } from '@fluentui/react/lib/DetailsList';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { HierarchicalEntityChild } from 'src/luis/projects/entities/types/HierarchicalEntity';
import { LuisId } from 'src/luis/types/Resource';
import { HierarchicalEntityInputField } from 'src/luis/verticals/entityDetails/components/hierarchicalEntityDetails/HierarchicalEntityInputField';

export type HierarchicalEntityChildrenTableProps = TableProps<HierarchicalEntityChild> & {
    /**
     * The callback called to rename a given child.
     */
    onRenameChild: (childId: LuisId, childName: string) => void;
};
const messages = defineMessages({
    childEntityNameColumnHeader: {
        id: 'entities.hierarchicalEntityChildTable.childEntityNameColumnHeader',
        defaultMessage: 'Child Entity'
    }
});

/**
 * Represents a table of children entities for a hierarchical entity.
 *
 * @param props The properties to pass to the table.
 */
export const HierarchicalEntityChildTable = (props: HierarchicalEntityChildrenTableProps) => {
    const { items, selection, onRenameChild } = props;
    const { localize } = useLocalization();

    /**
     * Renders the child name column.
     */
    const onRenderChildNameColumn: IDetailsListProps['onRenderItemColumn'] = (item: HierarchicalEntityChild) => (
        <HierarchicalEntityInputField childId={item.id} key={item.id} childName={item.name} onChange={onRenameChild} />
    );

    const columns: IColumn[] = [
        {
            fieldName: 'name',
            key: 'childEntityColumn',
            name: localize(messages.childEntityNameColumnHeader),
            minWidth: 150,
            isRowHeader: true,
            onRender: onRenderChildNameColumn
        }
    ];

    return <Table<HierarchicalEntityChild> items={items} selection={selection} columns={columns} />;
};
